import React from 'react';
import { Icon } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../../styles/glassmorphism';
import { TimeSeriesData } from '../types/dashboard.types';

interface MetricsChartProps {
  data: TimeSeriesData[];
  metric: 'sent' | 'delivered' | 'read' | 'accepted';
}

const MetricsChart: React.FC<MetricsChartProps> = ({ data, metric }) => {
  const metricConfig = {
    sent: { label: 'Enviadas', color: '#3498db', key: 'messagesSent' },
    delivered: { label: 'Entregues', color: '#9b59b6', key: 'messagesDelivered' },
    read: { label: 'Lidas', color: '#e67e22', key: 'messagesRead' },
    accepted: { label: 'Aceitas', color: '#27ae60', key: 'offersAccepted' }
  };

  const config = metricConfig[metric] as any;
  const values = data.map(d => (d as any)[config.key]);
  const maxValue = Math.max(...values, 1);
  const chartHeight = 160;
  const padding = 40;
  const chartWidth = Math.max(300, data.length * 30);

  // Calcular pontos do gráfico
  const points = values.map((value, idx) => {
    const x = padding + (idx / Math.max(data.length - 1, 1)) * (chartWidth - 2 * padding);
    const y = chartHeight - padding - (value / maxValue) * (chartHeight - 2 * padding);
    return { x, y, value };
  });

  // Criar path SVG
  const pathD = points.reduce((acc, point, idx) => {
    if (idx === 0) return `M ${point.x} ${point.y}`;
    return `${acc} L ${point.x} ${point.y}`;
  }, '');

  return (
    <div
      style={{
        ...GlassmorphismTheme.components.glassCard,
        background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1), rgba(26, 188, 156, 0.1))',
        border: GlassmorphismTheme.borders.glassAccent,
        padding: GlassmorphismTheme.spacing.md,
      }}
    >
      <h4 style={{
        margin: `0 0 ${GlassmorphismTheme.spacing.md} 0`,
        color: '#2c3e50',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Icon name="chart line" style={{ color: config.color }} />
        {config.label} (Últimos 7 dias)
      </h4>

      {/* CHART */}
      <div style={{ overflowX: 'auto', marginBottom: GlassmorphismTheme.spacing.lg }}>
        <svg
          width={chartWidth}
          height={chartHeight}
          style={{
            minWidth: '100%',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: GlassmorphismTheme.borderRadius.sm,
            border: `1px solid ${config.color}30`
          }}
        >
          {/* GRID */}
          {[0, 25, 50, 75, 100].map((pct, idx) => {
            const y = chartHeight - padding - (pct / 100) * (chartHeight - 2 * padding);
            return (
              <g key={`grid-${idx}`}>
                <line
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeDasharray="4"
                />
                <text
                  x={padding - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#95a5a6"
                >
                  {Math.round((pct / 100) * maxValue)}
                </text>
              </g>
            );
          })}

          {/* LINE */}
          <path
            d={pathD}
            fill="none"
            stroke={config.color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              filter: `drop-shadow(0 2px 4px ${config.color}40)`
            }}
          />

          {/* AREA FILL */}
          <path
            d={`${pathD} L ${points[points.length - 1]?.x} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`}
            fill={`${config.color}20`}
          />

          {/* POINTS */}
          {points.map((point, idx) => (
            <circle
              key={`point-${idx}`}
              cx={point.x}
              cy={point.y}
              r="4"
              fill={config.color}
              stroke="white"
              strokeWidth="2"
              style={{
                cursor: 'pointer',
                transition: 'r 0.2s'
              }}
              onMouseEnter={(e) => {
                (e.target as SVGCircleElement).r.baseVal.value = 6;
              }}
              onMouseLeave={(e) => {
                (e.target as SVGCircleElement).r.baseVal.value = 4;
              }}
            />
          ))}

          {/* X-AXIS LABELS */}
          {data.map((d, idx) => {
            if (idx % Math.ceil(data.length / 4) !== 0) return null;
            const x = padding + (idx / Math.max(data.length - 1, 1)) * (chartWidth - 2 * padding);
            const date = new Date(d.date);
            return (
              <text
                key={`label-${idx}`}
                x={x}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize="12"
                fill="#95a5a6"
              >
                {date.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })}
              </text>
            );
          })}
        </svg>
      </div>

      {/* STATS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: GlassmorphismTheme.spacing.md
      }}>
        <div style={{
          padding: GlassmorphismTheme.spacing.md,
          background: `${config.color}15`,
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          textAlign: 'center'
        }}>
          <small style={{ color: '#95a5a6' }}>Total</small>
          <p style={{
            margin: '6px 0 0 0',
            fontWeight: 600,
            color: config.color,
            fontSize: '1.2rem'
          }}>
            {values.reduce((a, b) => a + b, 0).toLocaleString('pt-BR')}
          </p>
        </div>
        <div style={{
          padding: GlassmorphismTheme.spacing.md,
          background: `${config.color}15`,
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          textAlign: 'center'
        }}>
          <small style={{ color: '#95a5a6' }}>Máximo</small>
          <p style={{
            margin: '6px 0 0 0',
            fontWeight: 600,
            color: config.color,
            fontSize: '1.2rem'
          }}>
            {Math.max(...values).toLocaleString('pt-BR')}
          </p>
        </div>
        <div style={{
          padding: GlassmorphismTheme.spacing.md,
          background: `${config.color}15`,
          borderRadius: GlassmorphismTheme.borderRadius.sm,
          textAlign: 'center'
        }}>
          <small style={{ color: '#95a5a6' }}>Média</small>
          <p style={{
            margin: '6px 0 0 0',
            fontWeight: 600,
            color: config.color,
            fontSize: '1.2rem'
          }}>
            {Math.round(values.reduce((a, b) => a + b, 0) / values.length).toLocaleString('pt-BR')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MetricsChart;
